import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  HostBinding,
  Input,
} from '@angular/core';
import { AnimationBehavior } from '@freelancer/directives';
import { ContainerSize } from '@freelancer/ui/container';
import { SectionArticleButtonComponent } from './section-article-button.component';
import { SectionArticleDescriptionComponent } from './section-article-description.component';
import { SectionArticleTitleComponent } from './section-article-title.component';
import {
  SectionArticleAlignment,
  SectionArticleColor,
  SectionArticleSize,
} from './section-article.types';

@Component({
  standalone: false,
  selector: 'fl-section-article',
  template: `
    <fl-container [size]="containerSize">
      @if (sectionArticleTitleComponent) {
        <fl-grid>
          <fl-col
            class="SectionArticle-heading"
            [col]="12"
          >
            <div
              [flAnimateOnScroll]="animateOnScroll"
              [flAnimateOnScrollBehavior]="animateOnScrollBehavior"
            >
              <h2
                class="text-body-24 tablet:text-title-32 desktop:text-title-40 {{
                  sectionArticleDescriptionComponent ? 'mb-xxsmall' : 'mb-large'
                }} {{
                  sectionArticleDescriptionComponent
                    ? 'tablet:mb-small'
                    : 'tablet:mb-large'
                }}"
                [class.text-foreground]="color === SectionArticleColor.DARK"
                [class.text-light]="!(color === SectionArticleColor.DARK)"
              >
                <ng-content select="fl-section-article-title"></ng-content>
              </h2>
              @if (sectionArticleDescriptionComponent) {
                <div
                  class="mb-large whitespace-pre-line text-body-16 desktop:text-body-20 font-normal {{
                    color === SectionArticleColor.DARK
                      ? 'text-foreground'
                      : 'text-light'
                  }}"
                >
                  <ng-content
                    select="fl-section-article-description"
                  ></ng-content>
                </div>
              }
            </div>
          </fl-col>
        </fl-grid>
      }
      <fl-grid
        [attr.data-text-align]="alignment"
        [attr.data-text-align-tablet]="alignmentTablet"
        [attr.data-text-align-desktop-small]="alignmentDesktopSmall"
        [attr.data-alignment]="alignment"
        class="{{ sectionArticleButtonComponent ? 'mb-xlarge' : 'mb-none' }}"
      >
        <ng-content></ng-content>
      </fl-grid>
      <fl-grid
        class="SectionArticle-button"
        [flAnimateOnScroll]="animateOnScroll"
        [flAnimateOnScrollBehavior]="animateOnScrollBehavior"
      >
        <fl-col [col]="12">
          <ng-content select="fl-section-article-button"></ng-content>
        </fl-col>
      </fl-grid>
    </fl-container>
  `,
  styleUrls: ['./section-article.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionArticleComponent {
  SectionArticleColor = SectionArticleColor;

  @HostBinding('attr.data-text-align')
  @Input()
  alignment: SectionArticleAlignment;
  @HostBinding('attr.data-text-align-tablet')
  @Input()
  alignmentTablet?: SectionArticleAlignment;
  @HostBinding('attr.data-text-align-desktop-small')
  @Input()
  alignmentDesktopSmall?: SectionArticleAlignment;
  @Input() containerSize: ContainerSize = ContainerSize.DESKTOP_LARGE;
  @Input() animateOnScroll = false;
  @Input() animateOnScrollBehavior: AnimationBehavior =
    AnimationBehavior.FADE_FROM_BOTTOM;

  @HostBinding('attr.data-size')
  @Input()
  size?: SectionArticleSize;

  @Input() color: SectionArticleColor = SectionArticleColor.DARK;

  @ContentChild(SectionArticleTitleComponent)
  sectionArticleTitleComponent: SectionArticleTitleComponent;
  @ContentChild(SectionArticleDescriptionComponent)
  sectionArticleDescriptionComponent: SectionArticleDescriptionComponent;
  @ContentChild(SectionArticleButtonComponent)
  sectionArticleButtonComponent: SectionArticleButtonComponent;
}
